import React, { use } from "react";
import { useAtomValue } from "jotai";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import {
    CreateMissionScreensContext,
    lastSalaryDetailsState,
} from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { View, StyleSheet } from "react-native";
import Text from "../../components/Text";
import TextField from "../../components/TextField";
import SalaryBreakdown from "../../components/editors/salary/SalaryBreakdown";
import { EarningType } from "../../__generated__/graphql";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { GENERATE_MISSION_SIMULATION_EMAIL } from "../../graphql/core";
import { useMutation } from "@apollo/client/react";
import { createMissionInputFromMission, MissionWithIjm } from "../../utils";
import { useSnackbar } from "../../hooks/snackbar";
import Switch from "../../ui_atoms/Switch";
import Gap from "../../ui_atoms/Gap";
import { useTheme } from "../../hooks/use-theme";

const CreateMissionSimulationSummary = () => {
    const i18n = useI18n();
    const mission = useAtomValue(newMissionState);
    const { closeWizard } = use(CreateMissionScreensContext);
    const { user } = use(AuthManagerContext);
    const { fonts } = useTheme();

    const [email, setEmail] = React.useState<string>(user?.email || "");
    const [isSendingEmail, setIsSendingEmail] = React.useState(false);
    const [includePdfDetails, setIncludePdfDetails] = React.useState(true);
    const [includeContractExamples, setIncludeContractExamples] = React.useState(true);
    const [generateMissionSimulationEmail] = useMutation(GENERATE_MISSION_SIMULATION_EMAIL);
    const { showSnackbar } = useSnackbar();

    const handleSendEmail = React.useCallback(async () => {
        setIsSendingEmail(true);
        try {
            const result = await generateMissionSimulationEmail({
                variables: {
                    missionData: createMissionInputFromMission(mission as MissionWithIjm),
                    workerData: {
                        birthdate: mission?.workerInfo?.birthdate,
                        gender: mission?.workerInfo?.gender,
                    },
                    email: email,
                    includePdfDetails: includePdfDetails,
                    includeContractExamples: includeContractExamples,
                },
            });
            if (result.data?.generateAndSendMissionSimulationPdf) {
                showSnackbar({
                    text: i18n.t(
                        "screens.user.create_mission_simulation_summary.send_email_success",
                    ),
                    mode: "success",
                });
            }
            closeWizard(true);
        } catch (error) {
            console.error("Error sending email:", error);
        } finally {
            setIsSendingEmail(false);
        }
    }, [
        closeWizard,
        email,
        generateMissionSimulationEmail,
        i18n,
        mission,
        showSnackbar,
        includePdfDetails,
        includeContractExamples,
    ]);

    const origin = useBubbleStepOrigin("CreateMissionSimulationSummary");

    const isEmailValid = email.includes("@") && email.includes(".");
    const salaryDetails = useAtomValue(lastSalaryDetailsState);
    const styles = StyleSheet.create({
        container: {},
        summarySection: {
            marginBottom: 24,
        },
        sectionTitle: {
            ...fonts.headlineSmall,
            fontSize: 18,
            marginBottom: 12,
        },
        emailSection: {
            marginTop: 16,
        },
        emailDescription: {
            ...fonts.bodySmall,
            marginBottom: 16,
        },
        optionsSection: {},
        switchRow: {
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            paddingVertical: 12,
        },
        switchDescription: {
            ...fonts.bodySmall,
        },
        salaryInfo: {
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
        },
        salaryText: {
            fontSize: 16,
            fontWeight: "600",
            textAlign: "center",
        },
    });

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("screens.user.create_mission_simulation_summary.send_email"),
                    onPress: handleSendEmail,
                    isValid: isEmailValid && (includePdfDetails || includeContractExamples),
                    validating: isSendingEmail,
                },
            ]}
            withAssistanceButton
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_simulation_summary.title")}
                origin={origin}
            >
                <View style={styles.container}>
                    {/* Section résumé du salaire simulé */}
                    <View style={styles.summarySection}>
                        <Text style={styles.sectionTitle}>
                            {i18n.t(
                                "screens.user.create_mission_simulation_summary.salary_summary",
                            )}
                        </Text>
                        {salaryDetails && (
                            <SalaryBreakdown
                                earningType={mission?.currentContract?.earningType as EarningType}
                                salaryDetails={salaryDetails}
                            />
                        )}
                    </View>

                    {/* Section email */}
                    <View style={styles.emailSection}>
                        <Text style={styles.sectionTitle}>
                            {i18n.t("screens.user.create_mission_simulation_summary.email_results")}
                        </Text>
                        <Text style={styles.emailDescription}>
                            {i18n.t(
                                "screens.user.create_mission_simulation_summary.email_description",
                            )}
                        </Text>
                        {/* Section options d'inclusion */}
                        <View style={styles.optionsSection}>
                            <View style={styles.switchRow}>
                                <Switch value={includePdfDetails} onChange={setIncludePdfDetails} />
                                <Gap mode="horizontal" gap={1 / 2} />
                                <Text style={styles.switchDescription}>
                                    {i18n.t(
                                        "screens.user.create_mission_simulation_summary.include_pdf_details",
                                    )}
                                </Text>
                            </View>
                            <View style={styles.switchRow}>
                                <Switch
                                    value={includeContractExamples}
                                    onChange={setIncludeContractExamples}
                                />
                                <Gap mode="horizontal" gap={1 / 2} />
                                <Text style={styles.switchDescription}>
                                    {i18n.t(
                                        "screens.user.create_mission_simulation_summary.include_contract_examples",
                                    )}
                                </Text>
                            </View>
                        </View>
                        <TextField
                            label={i18n.t("user.email")}
                            value={email}
                            onChangeText={setEmail}
                            placeholder={i18n.t(
                                "screens.user.create_mission_simulation_summary.email_placeholder",
                            )}
                            keyboardType="email-address"
                            autoCapitalize="none"
                        />
                    </View>
                </View>
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionSimulationSummary;
